import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { createTheme, ThemeProvider, CssBaseline, Box, useMediaQuery, IconButton, Tooltip } from '@mui/material';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Landing from './UI/Pages/Landing';
import FixtureDetails from './UI/Pages/FixtureDetails';
import Privacy from './UI/Pages/Privacy';
import Terms from './UI/Pages/Terms';
import NavBar from './UI/Components/NavBar';
import Footer from './UI/Components/Footer';

const IS_PROD = process.env.REACT_APP_STAGE === 'production';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}


function buildTheme(prefersDark) {
  return createTheme({
    palette: {
      mode: prefersDark ? 'dark' : 'light',
      background: prefersDark
        ? { default: '#121212', paper: '#1e1e1e' }
        : { default: '#f5f5f5', paper: '#ffffff' },
      divider: prefersDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: 'none',
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
          }),
        },
      },
    },
  });
}

export default function App() {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [modeOverride, setModeOverride] = React.useState(null); // null | 'light' | 'dark'
  const isDark = modeOverride !== null ? modeOverride === 'dark' : prefersDark;
  const theme = React.useMemo(() => buildTheme(isDark), [isDark]);

  const toggleMode = () => setModeOverride(isDark ? 'light' : 'dark');

  return (
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ScrollToTop />
        <NavBar />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/fixture/:id" element={<FixtureDetails />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
        <Footer />
      </BrowserRouter>
      {!IS_PROD && (
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} placement="left">
          <IconButton
            onClick={toggleMode}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {isDark ? <LightModeRounded /> : <DarkModeRounded />}
          </IconButton>
        </Tooltip>
      )}
    </ThemeProvider>
    </HelmetProvider>
  );
}
